/* MagicLink — passwordless sign-in.
 *
 *   GET  /magic-link            → email-input form (request flow)
 *   GET  /magic-link?token=XYZ  → auto-verifies, then redirects
 *
 * Backend: POST /api/auth/magic-link/{request,verify}
 *
 * Same security model as password-reset (token_urlsafe, 15-min expiry,
 * single-use, SendGrid click-tracking off).
 */
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Loader2, CheckCircle2, AlertCircle, ArrowLeft, KeyRound } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COPY = {
  bg: {
    title: "Вход без парола",
    subtitle: "Ще ви изпратим линк за вход на имейла.",
    email: "Имейл",
    request: "Изпрати ми линк",
    requesting: "Изпращане...",
    sent: "Готово! Проверете пощата си — линкът е валиден 15 минути.",
    verifying: "Влизаме във вашия акаунт...",
    invalid: "Линкът е невалиден или е изтекъл.",
    backToLogin: "Назад към вход с парола",
    requestNew: "Заявете нов линк",
  },
  en: {
    title: "Sign in without a password",
    subtitle: "We'll email you a one-click sign-in link.",
    email: "Email",
    request: "Email me the link",
    requesting: "Sending...",
    sent: "Done! Check your inbox — the link is valid for 15 minutes.",
    verifying: "Signing you in...",
    invalid: "Link is invalid or has expired.",
    backToLogin: "Back to password sign-in",
    requestNew: "Request a new link",
  },
  es: {
    title: "Inicia sesion sin contrasena",
    subtitle: "Te enviaremos un enlace de inicio por correo.",
    email: "Correo",
    request: "Enviarme el enlace",
    requesting: "Enviando...",
    sent: "Listo! Revisa tu correo — el enlace es valido por 15 minutos.",
    verifying: "Iniciando sesion...",
    invalid: "Enlace invalido o caducado.",
    backToLogin: "Volver al inicio con contrasena",
    requestNew: "Solicitar un nuevo enlace",
  },
};

export default function MagicLink() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const { language } = useLanguage();
  const { setUser } = useAuth();
  const t = COPY[language] || COPY.bg;

  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(token ? "verifying" : "form"); // form | sent | verifying | error
  const [error, setError] = useState("");

  // ── Verify token branch ────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/auth/magic-link/verify`, { token });
        if (data?.access_token || data?.user_id || data?.email) {
          // Tokens are now set via HttpOnly cookie by the backend.
          // We just hydrate the user state and navigate.
          if (typeof setUser === "function") setUser(data);
          navigate(data.redirect_to || "/dashboard", { replace: true });
        } else {
          setStage("error");
          setError(t.invalid);
        }
      } catch (e) {
        setStage("error");
        setError(e?.response?.data?.detail || t.invalid);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ── Request branch ─────────────────────────────────────────────────
  const requestLink = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/magic-link/request`, { email: email.trim().toLowerCase() });
      setStage("sent");
    } catch (e2) {
      setError(e2?.response?.data?.detail || t.invalid);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <Card className="bg-white border-[#E2E8F0] shadow-xl">
          <CardContent className="p-8">
            <div className="flex flex-col items-center mb-6">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#0EA5E9] to-[#6366F1] flex items-center justify-center mb-3">
                <KeyRound className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-xl font-bold text-[#1A1A2E]" data-testid="magic-link-title">{t.title}</h1>
              <p className="text-sm text-[#64748B] mt-1 text-center">{t.subtitle}</p>
            </div>

            {stage === "verifying" && (
              <div className="flex flex-col items-center py-6 text-[#64748B]" data-testid="magic-link-verifying">
                <Loader2 className="w-7 h-7 animate-spin mb-3 text-[#0EA5E9]" />
                <span className="text-sm">{t.verifying}</span>
              </div>
            )}

            {stage === "error" && (
              <div className="space-y-3" data-testid="magic-link-error">
                <div className="flex items-center gap-2 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error || t.invalid}</span>
                </div>
                <Button
                  onClick={() => { setStage("form"); setError(""); }}
                  className="w-full h-11 gradient-primary text-white rounded-xl"
                  data-testid="magic-link-retry-btn"
                >
                  {t.requestNew}
                </Button>
              </div>
            )}

            {stage === "sent" && (
              <div className="space-y-4" data-testid="magic-link-sent">
                <div className="flex items-start gap-2 p-3 bg-emerald-50 text-emerald-700 rounded-lg text-sm">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{t.sent}</span>
                </div>
              </div>
            )}

            {stage === "form" && (
              <form onSubmit={requestLink} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ml-email" className="text-[#1A1A2E] text-sm font-medium">{t.email}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                    <Input
                      id="ml-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="magic-link-email-input"
                      className="h-11 pl-9 bg-[#F8FAFC] border-[#E2E8F0] rounded-xl"
                    />
                  </div>
                </div>
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
                <Button type="submit" disabled={busy} data-testid="magic-link-submit-btn"
                  className="w-full h-11 gradient-primary hover:opacity-90 text-white font-medium rounded-xl shadow-lg shadow-sky-500/20">
                  {busy ? t.requesting : t.request}
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-xs text-[#64748B] hover:text-[#0EA5E9] font-medium"
                data-testid="magic-link-back-to-login"
              >
                <ArrowLeft className="w-3 h-3" />
                {t.backToLogin}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
